import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TradeMatchingFileType,
  useListTradeMatchingFiles,
} from "../dataHooks/tradeMatchingQueries";
import { useTradeMatcherDateRange } from "../hooks/useTradeMatcherDateRange";
import { StatusChip } from "./statusChip";

const DATE_FORMAT = "YYYY-MM-DD";

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

const buildBlobPath = (
  fileType: TradeMatchingFileType,
  uploadDate: string,
  fileName: string,
): string => `trade-matching/${fileType}/${uploadDate}/${fileName}`;

type Props = {
  onProcessFile: (
    blobPath: string,
    fileType: TradeMatchingFileType,
  ) => void;
  processingPath: string | null;
  isPending: boolean;
};

const sourcePrefix = (fileType: TradeMatchingFileType): "DTCC_" | "Regis_" =>
  fileType === "dtcc" ? "DTCC_" : "Regis_";

const buildSource = (
  fileType: TradeMatchingFileType,
  uploadDate: string,
  fileName: string,
): string => `${sourcePrefix(fileType)}${uploadDate}_${fileName}`;

export const TradeMatchingFileList: FC<Props> = ({
  onProcessFile,
  processingPath,
  isPending,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [[from, to]] = useTradeMatcherDateRange();
  const fromString = from?.isValid() ? from.format(DATE_FORMAT) : null;
  const toString = to?.isValid() ? to.format(DATE_FORMAT) : null;

  const { data, isLoading, isFetching, error } = useListTradeMatchingFiles(
    fromString,
    toString,
  );

  const hasValidRange = fromString !== null && toString !== null;

  return (
    <Box>
      {!hasValidRange && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a date range to view uploaded files.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {isLoading && hasValidRange && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {data && (
        <TableContainer component={Paper} variant="outlined">
          {isFetching && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Upload date</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Last modified</TableCell>
                <TableCell>Uploader</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Matches</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                    >
                      No files uploaded in the selected date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((file) => {
                  const blobPath = buildBlobPath(
                    file.fileType,
                    file.uploadDate,
                    file.fileName,
                  );
                  const isThisRowProcessing =
                    isPending && processingPath === blobPath;
                  return (
                    <TableRow key={blobPath}>
                      <TableCell>{file?.fileName}</TableCell>
                      <TableCell>
                        <Chip
                          label={file?.fileType?.toUpperCase()}
                          size="small"
                          variant="filled"
                          color={file?.fileType === "dtcc" ? "info" : "warning"}
                        />
                      </TableCell>
                      <TableCell>{file.uploadDate}</TableCell>
                      <TableCell align="right">
                        {formatBytes(file.sizeBytes)}
                      </TableCell>
                      <TableCell>
                        {dayjs(file.lastModified).format("YYYY-MM-DD HH:mm")}
                      </TableCell>
                      <TableCell>{file.uploader ?? "—"}</TableCell>
                      <TableCell>
                        <StatusChip run={file.lastRun} />
                      </TableCell>
                      <TableCell align="right">
                        {file.matchCount
                          ? `${file.matchCount.matched}/${file.matchCount.total}`
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          sx={{ gap: 1, justifyContent: "flex-end" }}
                        >
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<OpenInNewIcon />}
                            onClick={() =>
                              navigate(
                                `file/${encodeURIComponent(
                                  buildSource(
                                    file.fileType,
                                    file.uploadDate,
                                    file.fileName,
                                  ),
                                )}${location.search}`,
                              )
                            }
                          >
                            Details
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              isThisRowProcessing ? (
                                <CircularProgress size={14} />
                              ) : (
                                <PlayArrowOutlinedIcon />
                              )
                            }
                            onClick={() =>
                              onProcessFile(blobPath, file.fileType)
                            }
                            disabled={isPending}
                          >
                            Process
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
